//client/src/components/wholesaler/ExportSettingsModal.js

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { X, Download, Save, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';

const ExportSettingsModal = ({ isOpen, onClose, orderToExport, ordersToExport }) => {
  const { t } = useTranslation('wholesaler');
  const isBulk = Array.isArray(ordersToExport) && ordersToExport.length > 0;

  const AVAILABLE_COLUMNS = [
    { id: 'sku', label: t('orders.export.colSku', 'SKU (Κωδικός Προϊόντος)'), defaultEnabled: true, defaultOrder: 1 },
    { id: 'name', label: t('orders.export.colName', 'Όνομα Προϊόντος'), defaultEnabled: true, defaultOrder: 2 },
    { id: 'quantity', label: t('orders.export.colQuantity', 'Ποσότητα'), defaultEnabled: true, defaultOrder: 3 },
    { id: 'unit', label: t('orders.export.colUnit', 'Μονάδα (ΤΕΜ/ΚΓ)'), defaultEnabled: true, defaultOrder: 4 },
    { id: 'price', label: t('orders.export.colPrice', 'Τιμή Μονάδας'), defaultEnabled: false, defaultOrder: 5 },
    { id: 'total', label: t('orders.export.colTotal', 'Συνολική Αξία'), defaultEnabled: false, defaultOrder: 6 },
    { id: 'category', label: t('orders.export.colCategory', 'Κατηγορία'), defaultEnabled: false, defaultOrder: 7 },
    { id: 'brand', label: t('orders.export.colBrand', 'Μάρκα'), defaultEnabled: false, defaultOrder: 8 },
  ];

  const [columns, setColumns] = useState(() => {
    const saved = localStorage.getItem('csvExportSettings');
    return saved ? JSON.parse(saved) : AVAILABLE_COLUMNS;
  });

  if (!isOpen) return null;

  const toggleColumn = (id) => {
    setColumns(cols => cols.map(col =>
      col.id === id ? { ...col, enabled: !col.enabled } : col
    ));
  };

  const changeOrder = (id, newOrder) => {
    setColumns(cols => cols.map(col =>
      col.id === id ? { ...col, defaultOrder: parseInt(newOrder) || 0 } : col
    ));
  };

  const saveSettings = () => {
    localStorage.setItem('csvExportSettings', JSON.stringify(columns));
    toast.success(t('orders.export.settingsSaved', 'Settings saved!'));
  };

  const resetSettings = () => {
    setColumns(AVAILABLE_COLUMNS);
    localStorage.removeItem('csvExportSettings');
  };

  const getActiveColumns = () => {
    return columns
      .filter(c => c.enabled)
      .sort((a, b) => a.defaultOrder - b.defaultOrder);
  };

  const mapItemRow = (item, activeColumns) => {
    return activeColumns.map(col => {
      switch (col.id) {
        case 'sku': return item.sku || '';
        case 'name': return item.name || 'Unknown';
        case 'quantity': return Number(item.quantity) || 0;
        case 'unit': return item.unit === 'unit' ? 'TEM' : (item.unit || '').toUpperCase();
        case 'price': return Number(item.price || item.unitPrice || 0);
        case 'total': return Number(item.totalPrice || 0);
        case 'category': return item.category || '';
        case 'brand': return item.brand || '';
        default: return '';
      }
    });
  };

  const downloadXlsx = (aoa, filename) => {
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Auto-size columns based on content
    const colWidths = [];
    for (const row of aoa) {
      row.forEach((cell, i) => {
        const len = String(cell ?? '').length;
        colWidths[i] = Math.max(colWidths[i] || 8, len + 2);
      });
    }
    ws['!cols'] = colWidths.map(w => ({ wch: Math.min(w, 50) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orders');
    XLSX.writeFile(wb, filename);
  };

  const handleExport = () => {
    const activeColumns = getActiveColumns();

    if (activeColumns.length === 0) {
      toast.error(t('orders.export.selectOneColumn', 'Please select at least one column.'));
      return;
    }

    if (isBulk) {
      handleBulkExport(activeColumns);
    } else {
      handleSingleExport(activeColumns);
    }
  };

  const handleSingleExport = (activeColumns) => {
    const headers = activeColumns.map(col => col.label);
    const aoa = [headers];

    for (const item of orderToExport.items) {
      aoa.push(mapItemRow(item, activeColumns));
    }

    downloadXlsx(aoa, `Order_${orderToExport.orderNumber}.xlsx`);
    onClose();
  };

  const handleBulkExport = (activeColumns) => {
    const headers = activeColumns.map(col => col.label);
    const colCount = activeColumns.length;
    const aoa = [];

    ordersToExport.forEach((order, index) => {
      if (index > 0) {
        // Blank separator row between orders
        aoa.push(new Array(colCount).fill(''));
      }

      // Order info header row
      const businessName = order.businessId?.businessName || 'Unknown';
      const vatNumber = order.businessId?.vatNumber || '-';
      const orderNumber = order.orderNumber || '-';

      const infoRow = new Array(colCount).fill('');
      infoRow[0] = `${t('orders.export.businessName', 'Business')}: ${businessName}`;
      if (colCount > 1) infoRow[1] = `${t('orders.export.vatNo', 'VAT')}: ${vatNumber}`;
      if (colCount > 2) infoRow[2] = `${t('orders.export.orderNo', 'Order #')}: ${orderNumber}`;
      if (colCount === 1) {
        infoRow[0] = `${businessName} | VAT: ${vatNumber} | #${orderNumber}`;
      } else if (colCount === 2) {
        infoRow[0] = `${businessName} | VAT: ${vatNumber}`;
        infoRow[1] = `#${orderNumber}`;
      }
      aoa.push(infoRow);

      // Column headers
      aoa.push(headers);

      // Item rows
      for (const item of (order.items || [])) {
        aoa.push(mapItemRow(item, activeColumns));
      }
    });

    downloadXlsx(aoa, `orders_export_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    onClose();
  };

  const title = isBulk
    ? t('orders.export.titleBulk', 'Εξαγωγή Επιλεγμένων Παραγγελιών')
    : t('orders.export.title', 'Προσαρμογή Εξαγωγής');

  const subtitle = isBulk
    ? t('orders.export.descriptionBulk', `Εξαγωγή ${ordersToExport.length} παραγγελιών. Κάθε παραγγελία θα εμφανίζεται με τα στοιχεία πελάτη και τα επιλεγμένα πεδία.`)
    : t('orders.export.description', 'Επιλέξτε στήλες και ορίστε τη σειρά τους για αντιστοίχιση με το πρότυπο εισαγωγής ERP (SoftOne).');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          <button onClick={onClose}><X className="w-6 h-6 text-gray-400" /></button>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          <p className="text-sm text-gray-600 mb-4">{subtitle}</p>

          {isBulk && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              {t('orders.export.bulkInfo', `${ordersToExport.length} παραγγελίες επιλεγμένες`)}
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center text-xs font-bold text-gray-500 uppercase pb-2 border-b">
              <div className="w-10 text-center">{t('orders.export.headerOn', 'Ναι')}</div>
              <div className="w-16 text-center">{t('orders.export.headerOrder', 'Σειρά')}</div>
              <div className="flex-1">{t('orders.export.headerColumn', 'Στήλη')}</div>
            </div>

            {columns.map(col => (
              <div key={col.id} className={`flex items-center gap-3 p-2 rounded ${col.enabled ? 'bg-blue-50' : 'bg-gray-50'}`}>
                <div className="w-10 flex justify-center">
                  <input
                    type="checkbox"
                    checked={col.enabled}
                    onChange={() => toggleColumn(col.id)}
                    className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500"
                  />
                </div>
                <div className="w-16">
                  <input
                    type="number"
                    min="1"
                    value={col.defaultOrder}
                    onChange={(e) => changeOrder(col.id, e.target.value)}
                    disabled={!col.enabled}
                    className="w-full p-1 border rounded text-center text-sm disabled:opacity-50"
                  />
                </div>
                <div className="flex-1 font-medium text-gray-700">{col.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 border-t bg-gray-50 rounded-b-lg flex justify-between items-center">
          <button onClick={resetSettings} className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm">
            <RotateCcw className="w-4 h-4" /> {t('orders.export.reset', 'Επαναφορά')}
          </button>

          <div className="flex gap-2">
            <button onClick={saveSettings} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 flex items-center gap-2">
              <Save className="w-4 h-4" /> {t('orders.export.savePreset', 'Αποθήκευση Προτύπου')}
            </button>
            <button onClick={handleExport} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 font-semibold shadow-sm">
              <Download className="w-4 h-4" /> {t('orders.export.exportXlsx', 'Εξαγωγή XLSX')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportSettingsModal;
